/* eslint-disable react/prop-types */
import classNames from 'classnames';
import omit from 'lodash/omit';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { compose } from 'redux';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { getListingsById } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import { MARKETPLACE_PATH } from '../../constants/marketplacePath';
import {
  getQueryParamNames,
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
} from '../../util/search';
import { propTypes } from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
  stringify,
} from '../../util/urlHelpers';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { LISTING_CATEGORY, LISTING_STATUS, LISTING_TYPE } from '../../constants/listing.constants';

import {
  Breadcrumbs,
  H3,
  H5,
  LayoutSingleColumn,
  ModalInMobile,
  NamedRedirect,
  Page,
} from '../../components';
import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import {
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  getDatesAndSeatsMaybe,
  getSEOPagePath,
  getCurrentSEOPageType,
  groupListingFieldConfigs,
  initialValues,
  omitLimitedListingFieldParams,
  pickListingFieldFilters,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
} from './SearchPage.shared';
import { getLocaleFromUrl, IS_PRODUCTION } from '../../constants/locales.constants';

import SortBy from './SortBy/SortBy';
import CategoryFilter from '../../components/SearchFilters/filters/CategoryFilter';
import FilterComponent from './FilterComponent';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import SecondaryFilters from './SecondaryFilters/SecondaryFilters';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import BrandDropdown from '../../components/SearchFilters/BrandDropdown/BrandDropdown';
import IconFilters from '../../components/IconFilters/IconFilters';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    history.push(
      createResourceLocatorString(
        'SearchPage',
        routeConfiguration,
        {},
        {
          page: undefined,
          type: [],
          category: [],
          year: undefined,
          status: 'forSale',
          manufacturer: undefined,
        }
      )
    );
  }

  getHandleChangedValueFn(useHistoryPush) {
    const { history, routeConfiguration, config } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;

        // If user is changing brand or category filter, exclude the route-based value
        // to ensure the new filter value takes precedence
        let baseQueryParams = { ...urlQueryParams };
        if (updatedURLParams.manufacturer !== undefined) {
          // Remove route-based manufacturer when user is changing it
          delete baseQueryParams.manufacturer;
        }
        if (updatedURLParams.pub_category !== undefined) {
          // Remove route-based category when user is changing it
          delete baseQueryParams.pub_category;
        }

        const mergedQueryParams = { ...baseQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        const datesAndSeatsMaybe = getDatesAndSeatsMaybe(mergedQueryParams, updatedURLParams);

        const finalParams = {
          ...mergedQueryParams,
          ...updatedURLParams,
          ...keywordsMaybe,
          ...datesAndSeatsMaybe,
          address,
          bounds,
          page: undefined, // reset pagination on filter change
        };

        return {
          currentQueryParams: omitLimitedListingFieldParams(finalParams, filterConfigs),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

          // Check if we're currently on an SEO page
          const locale = getLocaleFromUrl();
          const currentSEOPage = getCurrentSEOPageType(this.props.match, locale);
          const seoPath = getSEOPagePath(search, locale, currentSEOPage);

          if (seoPath) {
            // Redirect to clean SEO URL, preserving other query params if needed
            const { page, sort, manufacturer, pub_category, ...otherParams } = search;
            const queryString =
              Object.keys(otherParams).length > 0 ? `?${stringify(otherParams)}` : '';
            const localePrefix = IS_PRODUCTION
              ? MARKETPLACE_PATH
              : locale
              ? `${MARKETPLACE_PATH}/${locale}`
              : '';
            history.push(`${localePrefix}${seoPath}${queryString}`);
          } else if (currentSEOPage) {
            // We're on an SEO page but changing different filter type or same value
            // Stay on current page and add query params
            const { page, sort, ...otherParams } = search;
            const queryString =
              Object.keys(otherParams).length > 0 ? `?${stringify(otherParams)}` : '';
            const localePrefix = IS_PRODUCTION
              ? MARKETPLACE_PATH
              : locale
              ? `${MARKETPLACE_PATH}/${locale}`
              : '';
            history.push(`${localePrefix}/${currentSEOPage.slug}${queryString}`);
          } else {
            // Regular search page behavior
            history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
          }
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      extraResultsHeader,
      // Set by SEO landing pages (see BrandModelPage) to override the page's
      // metadata and make it indexable. Absent everywhere else, so the plain
      // search page keeps its generic title and its noindex default.
      seo,
    } = this.props;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      isOriginInUse(config)
    );
    const validQueryParams = urlQueryParams;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const builtInPrimaryFilters = defaultFiltersConfig.filter(f =>
      ['categoryLevel'].includes(f.key)
    );
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(f => !['keywords', 'categoryLevel'].includes(f.key))
      : defaultFiltersConfig.filter(f => !['categoryLevel'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
      />
    );

    const { schema, title } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config
    );

    // An SEO landing page overrides the title/description and adds its own
    // schema nodes; Page accepts an array and spreads it into its @graph.
    const pageTitle = seo?.title || title;
    const pageDescription =
      seo?.description || intl.formatMessage({ id: 'SearchPage.schemaDescription' });
    const pageSchema = seo?.extraSchema?.length ? [schema, ...seo.extraSchema] : schema;

    const renderPriceComponent = ({ onToggle }) => (
      <div className={css.filterColumnContent}>
        {availableFilters.map(filterConfig => {
          const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
            filterConfig.key
          }`;
          return (
            <FilterComponent
              key={key}
              idPrefix="SearchFiltersDesktop"
              className={css.filter}
              config={filterConfig}
              listingCategories={listingCategories}
              marketplaceCurrency={marketplaceCurrency}
              urlQueryParams={validQueryParams}
              initialValues={initialValues(this.props, this.state.currentQueryParams)}
              getHandleChangedValueFn={this.getHandleChangedValueFn}
              intl={intl}
              liveEdit
              showAsPopup={false}
              isDesktop
              onToggle={onToggle}
            />
          );
        })}
      </div>
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;
    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={pageDescription}
        title={pageTitle}
        schema={pageSchema}
        noIndex={seo?.noIndex ?? true}
      >
        <LayoutSingleColumn
          topbar={
            <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
          }
          footer={<FooterContainer />}
        >
          <div className={css.layoutWrapperContainer}>
            <div className={css.layoutWrapperMain} role="main">
              <Breadcrumbs
                className={css.breadcrumbs}
                links={[
                  {
                    id: 'link 1',
                    label: <FormattedMessage id="Global.home" />,
                    name: 'LandingPage',
                  },
                  {
                    id: 'link 2',
                    label: <FormattedMessage id="General.marketplace" />,
                    name: 'SearchPage',
                    to: { search: `status=${LISTING_STATUS.FOR_SALE}` },
                  },
                  {
                    id: 'link 3',
                    label: <FormattedMessage id="Global.forSale" />,
                    name: 'SearchPage',
                    to: { search: `status=${LISTING_STATUS.FOR_SALE}` },
                  },
                ]}
              />
              <div className={css.searchResultContainer}>
                {extraResultsHeader}
                <MainPanelHeader
                  className={css.mainPanel}
                  // SEO landing pages supply their own h1; absent elsewhere.
                  heading={seo?.heading}
                  sortByComponent={sortBy('mobile')}
                  isSortByActive={sortConfig.active}
                  listingsAreLoaded={listingsAreLoaded}
                  resultsCount={totalItems}
                  searchInProgress={searchInProgress}
                  searchListingsError={searchListingsError}
                  noResultsInfo={noResultsInfo}
                  onManageDisableScrolling={onManageDisableScrolling}
                  renderPriceComponent={renderPriceComponent}
                  handleResetAllFilters={e => this.handleResetAll(e)}
                />

                <div
                  className={classNames(css.listingsForGridVariant, {
                    [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                  })}
                >
                  {searchListingsError ? (
                    <H3 className={css.error}>
                      <FormattedMessage id="SearchPage.searchError" />
                    </H3>
                  ) : null}
                  {!isValidDatesFilter ? (
                    <H5>
                      <FormattedMessage id="SearchPage.invalidDatesFilter" />
                    </H5>
                  ) : null}
                  <SearchResultsPanel
                    className={css.searchListingsPanel}
                    listings={listings}
                    pagination={listingsAreLoaded ? pagination : null}
                    search={parse(location.search)}
                    isMapVariant={false}
                    pageName={seo?.pageName}
                    pagePathParams={seo?.pagePathParams}
                  />
                </div>
              </div>
            </div>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

/**
 * SearchPage component with grid layout (no map)
 *
 * @param {Object} props
 * @param {propTypes.currentUser} [props.currentUser] - The current user
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {propTypes.pagination} [props.pagination] - The pagination
 * @param {boolean} [props.scrollingDisabled] - Whether the scrolling is disabled
 * @param {boolean} [props.searchInProgress] - Whether the search is in progress
 * @param {propTypes.error} [props.searchListingsError] - The search listings error
 * @param {Object} [props.searchParams] - The search params from the Redux state
 * @param {Function} [props.onManageDisableScrolling] - The function to manage the disable scrolling
 * @returns {JSX.Element}
 */
const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      match={match}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedSearchPage);

export default SearchPage;
